import * as React from 'react';
import { cn } from '@/utils/utils';

const WIDTHS = { sm: 'max-w-sm', md: 'max-w-md' } as const;

interface PageFrameProps extends React.HTMLAttributes<HTMLDivElement> {
  /** content: a page of sections, each in a `container`; centred: one column in the middle of the screen. */
  layout: 'content' | 'centred';
  /** How wide a centred page's column is. */
  width?: keyof typeof WIDTHS;
  /** What the page is drawn on: the page surface, or a background of its own that className sets (the billing pages' gradient). */
  surface?: 'page' | 'own';
}

/**
 * A page's frame: at least as tall as the screen, on the page surface. A content page's sections each sit in a
 * `container`, which centres them as wide as the breakpoint they are past, with the page's gutters (src/index.css);
 * a centred page holds one column in the middle of the screen, sm or md wide, with the same gutters on every side.
 */
export function PageFrame({ layout, width = 'md', surface = 'page', className, children, ...props }: PageFrameProps) {
  const page = cn('min-h-dvh', surface === 'page' && 'page-surface');
  if (layout === 'content') {
    return (
      <div className={cn(page, className)} {...props}>
        {children}
      </div>
    );
  }
  return (
    <div className={cn(page, 'flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8', className)} {...props}>
      <div className={cn('flex w-full flex-col items-center', WIDTHS[width])}>{children}</div>
    </div>
  );
}
